import { faQuestionCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { ToastType } from 'react-toastify';

import type { TSource } from '@feathr/blackbox';
import type { Billable } from '@feathr/blackbox';
import {
  Button,
  Card,
  Fieldset,
  Form,
  Input,
  SaveButton,
  Spinner,
  toast,
  Toolbar,
  Tooltip,
} from '@feathr/components';
import BillingSource from '@feathr/extender/components/BillingSource';
import { useLocalUrl } from '@feathr/extender/state';
import { useToggle } from '@feathr/hooks';

import LegacyAddressInput from '../../LegacyAddressInput';
import PaymentMethodForm from '../../PaymentMethodForm';

import * as styles from './BillableEdit.css';

interface IProps {
  billable: Billable;
  // Accommodate legacy page component.
  onRedirect?: () => void;
}

function BillableEdit({ billable, onRedirect }: IProps) {
  const history = useHistory();
  const localUrl = useLocalUrl();
  const [adding, toggleAdding] = useToggle(false);
  const { t } = useTranslation();

  function handleRedirect() {
    const search = new URLSearchParams(window.location.search);
    const redirect = search.get('redirect');
    history.push(redirect ? redirect : localUrl('/settings/billing/configurations'));
  }

  const actions = (
    <Toolbar>
      <SaveButton<Billable>
        disabled={!billable.isDirty}
        method={'patch'}
        model={billable}
        onSave={onRedirect ? onRedirect : handleRedirect}
      />
      <Button onClick={onRedirect ? onRedirect : handleRedirect} type={'naked'}>
        Cancel
      </Button>
    </Toolbar>
  );

  const { source } = billable.get('stripe');

  return (
    <Form
      actions={actions}
      data-id={billable.get('id')}
      data-model={'Billable'}
      label={'Edit Billing Configuration settings'}
    >
      <Fieldset label={'Payment methods'}>
        {!adding ? (
          <Card>
            {billable.isPending ? (
              <Spinner size={38} />
            ) : source ? (
              <BillingSource
                billable={billable}
                className={styles.paymentMethod}
                key={source.id}
                onDelete={async () => {
                  unsetSource(billable);
                  billable.isPending = true;
                  await billable.patchDirty();
                  billable.isPending = false;
                  if (billable.isErrored) {
                    toast(billable.error?.message, { type: ToastType.ERROR });
                  } else {
                    toast('Payment method deleted.', { type: ToastType.SUCCESS });
                  }
                }}
                onUpdate={toggleAdding}
                source={source}
              />
            ) : (
              <>
                <p className={styles.empty}>Payment method has not been set.</p>
                <Button onClick={toggleAdding}>Add payment method</Button>
              </>
            )}
          </Card>
        ) : (
          <Card title={'Set payment method'}>
            <PaymentMethodForm
              onClose={toggleAdding}
              onSave={async (updatedSource) => {
                setSource(billable, updatedSource);
                await billable.patchDirty();
                if (billable.isErrored) {
                  toast(billable.error?.message, { type: ToastType.ERROR });
                } else {
                  toast('Payment method added.', { type: ToastType.SUCCESS });
                }
                toggleAdding();
              }}
            />
          </Card>
        )}
      </Fieldset>
      <Fieldset label={'Bill to'}>
        <Input
          attribute={'name'}
          isLoading={billable.isPending}
          label={'Name'}
          model={billable}
          type={'text'}
        />
        <Input
          attribute={'email'}
          isLoading={billable.isPending}
          label={'Email'}
          model={billable}
          type={'email'}
        />
      </Fieldset>
      <Fieldset
        label={
          <span>
            {t('Billing address')}
            <Tooltip
              className={styles.icon}
              title={t("This is your organization's physical address")}
            >
              <FontAwesomeIcon icon={faQuestionCircle} />
            </Tooltip>
          </span>
        }
      >
        <LegacyAddressInput<Billable>
          attribute={'address'}
          isLoading={billable.isPending}
          model={billable}
        />
      </Fieldset>
    </Form>
  );
}

function setSource(billable: Billable, source: TSource) {
  const stripe = billable.get('stripe');
  billable.set({ stripe: { ...stripe, source, token: source.id } });
}

function unsetSource(billable: Billable) {
  const stripe = billable.get('stripe');
  billable.set({ stripe: { ...stripe, source: undefined, token: null } });
}

export default observer(BillableEdit);
